/**
 * @fileoverview TransactionList component displays transactions with collapsible details
 * on mobile. Shows type, amount, description, and paidBy by default. Click to expand
 * and reveal category breakdown, date, and edit/delete buttons.
 */

import { useState, useEffect, useCallback } from 'react';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/moneyUtils';
import { onBudgetContextChange } from '../../services/api';
import LoadingSkeleton from '../LoadingSkeleton/LoadingSkeleton';
import TransactionFilterPanel from './TransactionFilterPanel';
import EditTypeSelector from './EditTypeSelector';
import EditBasicFields from './EditBasicFields';
import AdjustmentFields from '../TransactionForm/AdjustmentFields';
import EditCategoryList from './EditCategoryList';
import ErrorMessages from '../TransactionForm/ErrorMessages';
import styles from './TransactionList.module.css';

/**
 * Apply filters to transactions array
 * All filters combine with AND logic
 * Category filter uses OR logic (match ANY selected category)
 */
const applyFilters = (transactions, filters) => {
    return transactions.filter(t => {
        // Who Paid filter (use == for loose comparison to handle string/number mismatch)
        if (filters.whoPaid !== null && String(t.paidByUserKey) !== String(filters.whoPaid)) {
            return false;
        }
        // Who Created filter (use == for loose comparison to handle string/number mismatch)
        if (filters.whoCreated !== null && String(t.createdBy) !== String(filters.whoCreated)) {
            return false;
        }
        // Transaction Type filter (OR within types)
        if (filters.transactionTypes.length > 0 && !filters.transactionTypes.includes(t.type)) {
            return false;
        }
        // Category filter (OR - match ANY selected category)
        if (filters.categories.length > 0) {
            const hasMatch = t.categories.some(cat => filters.categories.includes(cat.category));
            if (!hasMatch) return false;
        }
        return true;
    });
};

/**
 * Count active filters for badge display
 */
const getActiveFilterCount = (filters) => {
    let count = 0;
    if (filters.whoPaid !== null) count++;
    if (filters.whoCreated !== null) count++;
    if (filters.categories.length > 0) count++;
    if (filters.transactionTypes.length > 0) count++;
    return count;
};

/**
 * TransactionList Component
 *
 * Displays the list of transactions for the current month.
 * Transactions are shown in read-only view mode with edit buttons that open a modal.
 *
 * Props:
 * - transactions (array): List of transactions to display
 * - transactionHook (object): All transaction hook functions and state
 * - userRole (string): User's role in the current budget
 * - userKey (number): Current user's key for ownership checks
 * - isLoading (boolean): Whether transactions are currently loading
 * - readOnly (boolean): Whether budget is in read-only mode (ceased)
 */

function TransactionList({ transactions, transactionHook, userRole, userKey, isLoading, readOnly }) {
    // Sort state
    const [sortBy, setSortBy] = useState('date'); // 'date' | 'amount'
    const [sortOrder, setSortOrder] = useState('desc'); // 'desc' | 'asc'

    // Filter state
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const [filters, setFilters] = useState({
        whoPaid: null,
        whoCreated: null,
        categories: [],
        transactionTypes: []
    });

    // Default filter state for resetting
    const getDefaultFilters = useCallback(() => ({
        whoPaid: null,
        whoCreated: null,
        categories: [],
        transactionTypes: []
    }), []);

    // Reset filters when budget changes
    useEffect(() => {
        const unsubscribe = onBudgetContextChange(() => {
            setFilters(getDefaultFilters());
            setFilterPanelOpen(false);
        });
        return () => unsubscribe();
    }, [getDefaultFilters]);

    // Lock body scroll when filter panel is open
    useEffect(() => {
        if (filterPanelOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [filterPanelOpen]);

    // Get users and categories from transactionHook for filter options
    const users = transactionHook?.users || [];
    const categories = transactionHook?.categories || [];

    // Calculate active filter count
    const activeFilterCount = getActiveFilterCount(filters);

    // Handle sort change from dropdown
    const handleSortChange = (e) => {
        const [newSortBy, newSortOrder] = e.target.value.split('-');
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
    };

    // Handle filter changes from panel
    const handleFiltersChange = (newFilters) => {
        setFilters(newFilters);
    };

    // Clear all filters
    const handleClearAllFilters = () => {
        setFilters(getDefaultFilters());
    };

    // Show loading skeleton while fetching transactions
    if (isLoading) {
        return (
            <div className={styles.container}>
                <h3 className={styles.title}>Recent Transactions</h3>
                <LoadingSkeleton variant="list-item" count={5} />
            </div>
        );
    }

    // Apply filters first, then sort
    const filteredTransactions = applyFilters(transactions, filters);

    // Sort transactions
    const sortedTransactions = [...filteredTransactions].sort((a, b) => {
        if (sortBy === 'amount') {
            // Sort by absolute amount value
            const amountA = Math.abs(a.amount);
            const amountB = Math.abs(b.amount);
            const diff = sortOrder === 'desc' ? amountB - amountA : amountA - amountB;
            // Use date as tiebreaker
            if (diff === 0) {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
            }
            return diff;
        } else {
            // Sort by date, then by createdAt
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            const createdAtA = new Date(a.createdAt);
            const createdAtB = new Date(b.createdAt);

            if (sortOrder === 'desc') {
                if (dateB.getTime() !== dateA.getTime()) {
                    return dateB - dateA;
                }
                return createdAtB - createdAtA;
            } else {
                if (dateA.getTime() !== dateB.getTime()) {
                    return dateA - dateB;
                }
                return createdAtA - createdAtB;
            }
        }
    });

    // Show empty state only after loading is complete
    if (transactions.length === 0) {
        const today = new Date();
        const currentMonth = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

        return (
            <div className={styles.container}>
                <h3 className={styles.title}>Recent Transactions</h3>
                <p className={styles.emptyMessage}>No transactions for {currentMonth} yet. Add one above!</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h3 className={styles.title}>Recent Transactions</h3>
                    <span className={styles.transactionCount}>
                        ({filteredTransactions.length}{activeFilterCount > 0 ? ` of ${transactions.length}` : ''})
                    </span>
                </div>
                <div className={styles.headerRight}>
                    {/* Sort Dropdown */}
                    <select
                        value={`${sortBy}-${sortOrder}`}
                        onChange={handleSortChange}
                        className={styles.sortSelect}
                        aria-label="Sort transactions"
                    >
                        <option value="date-desc">Newest First</option>
                        <option value="date-asc">Oldest First</option>
                        <option value="amount-desc">Highest Amount</option>
                        <option value="amount-asc">Lowest Amount</option>
                    </select>

                    {/* Filter Button */}
                    <button
                        onClick={() => setFilterPanelOpen(true)}
                        className={styles.filterButton}
                        aria-label={`Filter transactions${activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}`}
                    >
                        <span className={styles.filterIcon}>⚙</span>
                        <span className={styles.filterText}>Filter</span>
                        {activeFilterCount > 0 && (
                            <span className={styles.filterBadge}>{activeFilterCount}</span>
                        )}
                    </button>
                </div>
            </div>

            {/* No results after filtering message */}
            {filteredTransactions.length === 0 && transactions.length > 0 && (
                <div className={styles.noResultsMessage}>
                    <p>No transactions match your filters.</p>
                    <button onClick={handleClearAllFilters} className={styles.clearFiltersLink}>
                        Clear all filters
                    </button>
                </div>
            )}

            {sortedTransactions.map(transaction => (
                <TransactionItem
                    key={transaction.id}
                    transaction={transaction}
                    transactionHook={transactionHook}
                    userRole={userRole}
                    userKey={userKey}
                    readOnly={readOnly}
                />
            ))}

            {/* Filter Panel */}
            <TransactionFilterPanel
                isOpen={filterPanelOpen}
                onClose={() => setFilterPanelOpen(false)}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClearAll={handleClearAllFilters}
                users={users}
                categories={categories}
            />
        </div>
    );
}

/**
 * TransactionItem Component
 *
 * Individual transaction row in read-only view mode.
 * Editing is now handled via modal popup instead of inline form.
 */
function TransactionItem({ transaction, transactionHook, userRole, userKey, readOnly }) {
    return <ViewTransaction transaction={transaction} transactionHook={transactionHook} userRole={userRole} userKey={userKey} readOnly={readOnly} />;
}

/**
 * ViewTransaction Component
 *
 * Displays a transaction in read-only mode with edit/delete buttons.
 * On mobile, details can be collapsed/expanded by clicking.
 */
function ViewTransaction({ transaction, transactionHook, userRole, userKey, readOnly }) {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleEdit = () => {
        transactionHook.startEdit(transaction);
    };

    const handleDelete = () => {
        transactionHook.deleteTransaction(transaction.id);
    };

    const toggleExpanded = (e) => {
        // Don't toggle if clicking on Edit or Delete button
        if (e.target.tagName === 'BUTTON') {
            return;
        }
        setIsExpanded(!isExpanded);
    };

    // Check if user can edit/delete this transaction
    // Members can only edit/delete their own transactions
    // Owners and elevated_members can edit/delete any transaction
    // Budget must not be in read-only mode (ceased)
    const canModify = !readOnly && (userRole !== 'member' || transaction.createdBy === userKey);

    // Build category display text
    let categoryInfo = '';
    if (transaction.type === 'Adjustment') {
        // For adjustments, find the from/to categories from the categories array
        const fromCategory = transaction.categories.find(cat => cat.amount < 0);
        const toCategory = transaction.categories.find(cat => cat.amount > 0);
        categoryInfo = `${fromCategory?.category || 'Unknown'} → ${toCategory?.category || 'Unknown'}`;
    } else {
        categoryInfo = transaction.categories
            .map(cat => `${cat.category}: ${cat.amount >= 0 ? '+' : ''}${formatCurrency(cat.amount)}`)
            .join(' | ');
    }

    // Get type badge class
    const typeBadgeClass = `${styles.typeBadge} ${
        transaction.type === 'Expense' ? styles.expense :
        transaction.type === 'Income' ? styles.income :
        transaction.type === 'Transfer' ? styles.transfer :
        transaction.type === 'Adjustment' ? styles.adjustment : ''
    }`;

    return (
        <div
            className={styles.transactionItem}
            onClick={toggleExpanded}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && toggleExpanded(e)}
        >
            <div className={styles.transactionContent}>
                <div className={styles.transactionInfo}>
                    <div className={styles.transactionMain}>
                        <span className={styles.transactionDate}>
                            {formatDateForDisplay(transaction.date)}
                        </span>
                        <span className={typeBadgeClass}>
                            {transaction.type || 'Expense'}
                        </span>
                        <span className={styles.transactionAmount}>
                            {formatCurrency(transaction.amount)}
                        </span>
                        {transaction.paidBy && (
                            <span className={styles.paidByBadge} title={transaction.paidBy}>
                                <span className={styles.paidByShort}>{transaction.paidBy.substring(0, 6)}</span>
                                <span className={styles.paidByFull}>{transaction.paidBy}</span>
                            </span>
                        )}
                        <span className={styles.transactionDescription}>
                            {transaction.description}
                        </span>
                        <span className={styles.chevron}>{isExpanded ? '▲' : '▼'}</span>
                    </div>
                    <div className={`${styles.transactionDetails} ${isExpanded ? styles.expanded : styles.collapsed}`}>
                        {transaction.description && (
                            <div className={styles.descriptionDetail}>
                                {transaction.description}
                            </div>
                        )}
                        <div className={styles.categoryDetail}>
                            {categoryInfo}
                        </div>
                        {transaction.paidBy && (
                            <span className={styles.paidByDetail}>
                                {'Paid by: '}{transaction.paidBy}
                            </span>
                        )}
                        {transaction.createdByUserName && (
                            <span className={styles.createdByDetail}>
                                {' | Created by: '}{transaction.createdByUserName}
                            </span>
                        )}
                    </div>
                </div>
                {canModify && (
                    <div className={`${styles.actionButtons} ${isExpanded ? styles.expanded : styles.collapsed}`}>
                        <button
                            onClick={handleEdit}
                            className={styles.editButton}
                        >
                            Edit
                        </button>
                        <button
                            onClick={handleDelete}
                            className={styles.deleteButton}
                        >
                            Delete
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * EditTransactionForm Component
 *
 * Inline edit form for transactions. Similar to TransactionForm but for editing.
 */
// eslint-disable-next-line no-unused-vars
function EditTransactionForm({ _transaction, transactionHook }) {
    const handleTypeChange = (value) => {
        transactionHook.setEditTransactionType(value);
        resetCategoriesOnTypeChange(value);
    };

    const resetCategoriesOnTypeChange = (newType) => {
        if (newType === 'Adjustment') {
            transactionHook.setEditTransactionCategories([{ category: '', amount: '' }]);
        } else if (newType === 'Transfer') {
            transactionHook.setEditTransactionCategories([{ category: '', amount: transactionHook.editAmount }]);
            transactionHook.setEditAdjustmentFromCategory('');
            transactionHook.setEditAdjustmentToCategory('');
        } else {
            transactionHook.setEditAdjustmentFromCategory('');
            transactionHook.setEditAdjustmentToCategory('');
        }
    };

    const handleAmountChange = (value) => {
        transactionHook.handlePositiveNumberInput(value, transactionHook.setEditAmount);
        updateTransferCategoryAmount(value);
    };

    const updateTransferCategoryAmount = (amount) => {
        const isTransfer = transactionHook.editTransactionType === 'Transfer';
        const hasSingleCategory = transactionHook.editTransactionCategories.length === 1;

        if (isTransfer && hasSingleCategory) {
            transactionHook.updateEditTransactionCategory(0, 'amount', amount);
        }
    };

    const handleAdjustmentFieldChange = (field, value) => {
        if (field === 'adjustmentFromCategory') {
            transactionHook.setEditAdjustmentFromCategory(value);
            transactionHook.clearEditError('adjustmentFromCategory');
        } else {
            transactionHook.setEditAdjustmentToCategory(value);
            transactionHook.clearEditError('adjustmentToCategory');
        }
        transactionHook.clearEditError('adjustmentCategories');
    };

    return (
        <div className={styles.editForm}>
            {transactionHook.editErrors.general && (
                <div className={styles.alert}>
                    {transactionHook.editErrors.general}
                </div>
            )}

            <EditTypeSelector
                transactionType={transactionHook.editTransactionType}
                currentUser={transactionHook.editCurrentUser}
                paidDirectly={transactionHook.editPaidDirectly}
                users={transactionHook.users}
                errors={transactionHook.editErrors}
                onTypeChange={handleTypeChange}
                onUserChange={transactionHook.setEditCurrentUser}
                onPaidDirectlyChange={transactionHook.setEditPaidDirectly}
                clearError={transactionHook.clearEditError}
            />

            <EditBasicFields
                transactionType={transactionHook.editTransactionType}
                amount={transactionHook.editAmount}
                description={transactionHook.editDescription}
                date={transactionHook.editDate}
                errors={transactionHook.editErrors}
                onAmountChange={handleAmountChange}
                onDescriptionChange={transactionHook.setEditDescription}
                onDateChange={transactionHook.setEditDate}
                clearError={transactionHook.clearEditError}
            />

            {transactionHook.editTransactionType === 'Adjustment' && (
                <AdjustmentFields
                    adjustmentFromCategory={transactionHook.editAdjustmentFromCategory}
                    adjustmentToCategory={transactionHook.editAdjustmentToCategory}
                    categories={transactionHook.categories}
                    errors={transactionHook.editErrors}
                    onFieldChange={handleAdjustmentFieldChange}
                    clearError={transactionHook.clearEditError}
                />
            )}

            {transactionHook.editTransactionType !== 'Adjustment' && (
                <EditCategoryList
                    transactionType={transactionHook.editTransactionType}
                    amount={transactionHook.editAmount}
                    categories={transactionHook.editTransactionCategories}
                    errors={transactionHook.editErrors}
                    onCategoryUpdate={transactionHook.updateEditTransactionCategory}
                    onCategoryAdd={transactionHook.addEditCategoryRow}
                    onCategoryRemove={transactionHook.removeEditCategoryRow}
                    getAvailableCategories={transactionHook.getAvailableCategories}
                    getAvailableCategoriesForExpenses={transactionHook.getAvailableCategoriesForExpenses}
                    getAvailableCategoriesForTransfers={transactionHook.getAvailableCategoriesForTransfers}
                    isGrandfatheredCategory={transactionHook.isGrandfatheredCategory}
                    clearCategoryError={transactionHook.clearEditCategoryError}
                />
            )}

            <div className={styles.editButtons}>
                <button onClick={transactionHook.saveEdit} className={styles.saveButton}>
                    Save
                </button>
                <button onClick={transactionHook.cancelEdit} className={styles.cancelButton}>
                    Cancel
                </button>
            </div>

            <ErrorMessages errors={transactionHook.editErrors} />
        </div>
    );
}

export default TransactionList;